



















































































import { ButtonGroup, VerticalFormBlock, TwSelect } from '@/app/components';
import { useQueryParams } from '@/app/composable';
import { useRouter } from '@/app/composable/router';
import { computed, defineComponent, onMounted, PropType, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import { Asset } from '../types';
import { Route } from 'vue-router';
import { match } from 'ts-pattern';

type Range = 'forever' | 'lastExecutions' | 'range';
type SearchQuery = {
    range: Range;
    startDate: Date | null;
    endDate: Date | null;
    limit: Number | null;
};

export default defineComponent({
    name: 'PeriodFilters',
    model: {
        prop: 'metricsQuery',
        event: 'update-query',
    },
    props: {
        asset: { type: Object as PropType<Asset>, required: true },
        metricsQuery: {
            type: Object as PropType<SearchQuery>,
        },
        lastExecutionsText: {
            type: String,
            default: 'Executions',
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        VerticalFormBlock,
        ButtonGroup,
        TwSelect,
    },
    setup(props: any, { root, emit }: { root: any; emit: any }) {
        const router = useRouter();
        const { get, setMany } = useQueryParams(root, router, 'assets:view');
        const minStartDate: Date = dayjs.utc(props.asset.createdAt).toDate();
        const maxEndDate: Date = dayjs.utc().toDate();

        const metricsQueryClone = ref<SearchQuery>({
            range: 'lastExecutions',
            startDate: null,
            endDate: null,
            limit: 15,
        });

        const qualityMetricsFilters = computed<{ value: string; label: string }[]>(() => [
            {
                value: 'lastExecutions',
                label: `Last ${props.lastExecutionsText}`,
            },
            { value: 'range', label: 'Range' },
            {
                value: 'forever',
                label: 'Forever',
            },
        ]);

        const limitOptions = ref<{ label: string; value: number }[]>([
            { label: '15', value: 15 },
            { label: '30', value: 30 },
            { label: '45', value: 45 },
            { label: '60', value: 60 },
            { label: '75', value: 75 },
            { label: '90', value: 90 },
        ]);

        const updateQueryParameters = () => {
            setMany(
                {
                    range: { value: metricsQueryClone.value.range },
                    start: {
                        value:
                            metricsQueryClone.value.range === 'range'
                                ? dayjs.utc(metricsQueryClone.value.startDate).toISOString()
                                : undefined,
                    },
                    end: {
                        value:
                            metricsQueryClone.value.range === 'range'
                                ? dayjs.utc(metricsQueryClone.value.endDate).toISOString()
                                : undefined,
                    },
                    limit: {
                        value:
                            metricsQueryClone.value.range === 'lastExecutions'
                                ? metricsQueryClone.value.limit?.toString()
                                : undefined,
                    },
                },
                true,
            );
        };

        const initialise = () => {
            const queryRange: Range = get('range');
            metricsQueryClone.value = match<Range, SearchQuery>(queryRange)
                .with('forever', () => ({
                    range: queryRange,
                    startDate: minStartDate,
                    endDate: maxEndDate,
                    limit: null,
                }))
                .with('range', () => {
                    const queryStart = get('start');
                    const queryEnd = get('end');
                    return {
                        range: queryRange,
                        startDate:
                            !queryStart || dayjs.utc(queryStart).isBefore(dayjs.utc(minStartDate))
                                ? minStartDate
                                : queryStart,
                        endDate:
                            !queryEnd || dayjs.utc(queryEnd).isAfter(dayjs.utc(maxEndDate)) ? maxEndDate : queryEnd,
                        limit: null,
                    };
                })
                .with('lastExecutions', () => {
                    const queryLimit = Number(get('limit'));
                    return {
                        range: 'lastExecutions',
                        startDate: null,
                        endDate: null,
                        limit: queryLimit && [15, 30, 45, 60, 75, 90].includes(queryLimit) ? queryLimit : 15,
                    };
                })
                .otherwise(() => ({
                    range: 'lastExecutions',
                    startDate: null,
                    endDate: null,
                    limit: 15,
                }));
            updateQueryParameters();
            emit('update-query', metricsQueryClone.value);
            emit('change');
        };

        const changeQuery = () => {
            metricsQueryClone.value = match<Range, SearchQuery>(metricsQueryClone.value.range)
                .with('forever', () => ({
                    range: 'forever',
                    startDate: null,
                    endDate: null,
                    limit: null,
                }))
                .with('lastExecutions', () => ({
                    range: 'lastExecutions',
                    startDate: null,
                    endDate: null,
                    limit: metricsQueryClone.value.limit ?? 15,
                }))
                .with('range', () => ({
                    range: 'range',
                    startDate: metricsQueryClone.value.startDate ?? minStartDate,
                    endDate: metricsQueryClone.value.endDate ?? maxEndDate,
                    limit: null,
                }))
                .otherwise(() => metricsQueryClone.value);
            updateQueryParameters();
            emit('update-query', metricsQueryClone.value);
            emit('change');
        };

        onMounted(() => initialise());

        useRouter().afterEach((to: Route, from: Route) => {
            if ((to.name !== 'assets:view' || to.query.tab !== 'dataProfiling') && from.query.tab === 'dataProfiling') {
                setMany(
                    {
                        range: { value: undefined },
                        start: { value: undefined },
                        end: { value: undefined },
                        limit: { value: undefined },
                    },
                    true,
                );
            }
        });

        return {
            metricsQueryClone,
            qualityMetricsFilters,
            minStartDate,
            maxEndDate,
            limitOptions,
            changeQuery,
        };
    },
});
